import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2 } from 'lucide-react';

let sessionToken = null;
const newSessionToken = () => {
  sessionToken = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
  return sessionToken;
};

export function AddressAutocompleteInput({ value, onChange, onSelect, placeholder = 'Start typing the address…', className = '', testId = 'address-autocomplete-input' }) {
  const [preds, setPreds] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const skipNextFetch = useRef(false);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    const q = (value || '').trim();
    if (q.length < 3) { setPreds([]); setOpen(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        if (!sessionToken) newSessionToken();
        const { data } = await api.get('/places/autocomplete', { params: { input: q, session_token: sessionToken } });
        setPreds(data.predictions || []);
        setOpen((data.predictions || []).length > 0);
      } catch {
        setPreds([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  const pick = async (p) => {
    setOpen(false);
    setLoading(true);
    try {
      const { data } = await api.get('/places/details', { params: { place_id: p.place_id, session_token: sessionToken } });
      sessionToken = null; // session complete per Google billing rules
      skipNextFetch.current = true;
      onChange?.(data.address || p.description);
      onSelect?.(data);
    } catch {
      skipNextFetch.current = true;
      onChange?.(p.description);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => { if (preds.length) setOpen(true); }}
        placeholder={placeholder}
        autoComplete="off"
        className={className}
        data-testid={testId}
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 th-spin text-white/50" />}
      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-white/12 bg-[#0B0C10] shadow-2xl" data-testid="address-autocomplete-dropdown">
          {preds.map((p) => (
            <button
              key={p.place_id}
              type="button"
              onClick={() => pick(p)}
              className="flex w-full items-start gap-2 border-b border-white/5 p-2.5 text-left last:border-b-0 hover:bg-white/5"
              data-testid={`address-prediction-${p.place_id}`}
            >
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#F6B24A]" />
              <span className="min-w-0">
                <span className="block truncate text-sm text-white">{p.main_text || p.description}</span>
                {p.secondary_text && <span className="block truncate text-xs text-white/55">{p.secondary_text}</span>}
              </span>
            </button>
          ))}
          <p className="bg-white/[0.03] px-2.5 py-1 text-right text-[9px] text-white/35">powered by Google</p>
        </div>
      )}
    </div>
  );
}
